<template>
    <v-overlay :model-value="loading" class="align-center justify-center" persistent>
        <v-progress-circular color="amber" indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <!-- VOLUNTEER FORM -->
    <img src="@/assets/pic.jpg" class="img-fluid formBanner" alt="Volunteer Application Banner">

    <div class="container outerContainer col-md-6 isForm">
        <div class="container innerContainer">

            <!-- Form Details -->
            <p id="eventFormHeader">APPLY AS A VOLUNTEER</p>
            <form class="row g-3" novalidate>
                <div class="col-md-6">

                    <!-- Full Name -->
                    <label for="inputFullName" class="form-label">Full Name</label>
                    <input type="text" class="form-control" id="inputFullName" placeholder="Full Name"
                        v-model="volunteerFormData.fullName" @input="volunteerForm$.fullName.$touch">
                    <div v-if="volunteerForm$.fullName.$errors[0]" class="alert alert-danger m-0 p-1">
                        <em> {{ volunteerForm$.fullName.$errors[0].$message }}</em>
                    </div>
                </div>

                <!-- Email -->
                <div class="col-md-6">
                    <label for="inputEmailAddress" class="form-label">E-mail Address</label>
                    <input type="email" class="form-control" id="inputEmailAddress" placeholder="E-mail Address"
                        v-model="volunteerFormData.email" @input="volunteerForm$.email.$touch">
                    <div v-if="volunteerForm$.email.$errors[0]" class="alert alert-danger m-0 p-1">
                        <em> {{ volunteerForm$.email.$errors[0].$message }}</em>
                    </div>
                </div>

                <!-- Contact Number -->
                <div class="col-md-6">
                    <label for="inputContactNumber" class="form-label">Contact Number</label>
                    <input type="text" class="form-control" id="inputContactNumber" placeholder="Contact Number"
                        v-model="volunteerFormData.contactNumber" @input="volunteerForm$.contactNumber.$touch">
                    <div v-if="volunteerForm$.contactNumber.$errors[0]" class="alert alert-danger m-0 p-1">
                        <em> {{ volunteerForm$.contactNumber.$errors[0].$message }}</em>
                    </div>
                </div>

                <!-- Category -->
                <div class="col-md-6">
                    <label for="selectEventCategory" class="form-label">Category</label>
                    <select class="form-select" id="selectEventCategory" v-model="volunteerFormData.category"
                        @blur="volunteerForm$.category.$touch">
                        <!-- Get each available category for volunteer, if not, show No Available Events -->
                        <option v-if="events.length == 0" value="" selected disabled hidden>No available events</option>
                        <option v-else value="" selected disabled hidden>Choose here</option>
                        <option v-for="event in events" :key="event.id"
                            :selected="event.id === volunteerFormData.category ? true : false" :value="event.Category">
                            {{ event.Category }}
                        </option>
                    </select>
                    <div v-if="volunteerForm$.category.$errors[0]" class="alert alert-danger m-0 p-1">
                        <em> {{ volunteerForm$.category.$errors[0].$message }}</em>
                    </div>
                </div>

                <!-- Valid ID -->
                <div class="mb-3">
                    <label for="inputValidID" class="form-label">Upload a Valid ID</label>
                    <input class="form-control" type="file" id="inputValidID" @change="onIdChange($event)" accept="image/*">
                    <div v-for="error in volunteerForm$.id.$errors" :key="error.$uid" class="alert alert-danger m-0 p-1">
                        <em> {{ error.$message }} </em>
                    </div>
                    <div v-for="error in volunteerForm$.idTitle.$errors" :key="error.$uid"
                        class="alert alert-danger m-0 p-1">
                        <em> {{ error.$message }} </em>
                    </div>
                </div>

                <!-- Upload CV/Resume -->
                <div class="mb-3">
                    <label for="inputResume" class="form-label">Upload CV/Resume</label>
                    <input class="form-control" type="file" id="inputResume" @change="onResumeChange($event)"
                        accept=".pdf, .doc, .docx">
                    <div v-for="error in volunteerForm$.resume.$errors" :key="error.$uid"
                        class="alert alert-danger m-0 p-1">
                        <em> {{ error.$message }} </em>
                    </div>
                    <div v-for="error in volunteerForm$.resumeTitle.$errors" :key="error.$uid"
                        class="alert alert-danger m-0 p-1">
                        <em> {{ error.$message }} </em>
                    </div>
                </div>

            </form>
        </div>
        <button type="submit" @click="submitVolunteerForm" id="registerButton">Register</button>
    </div>
</template>

<script>
// Dependencies
import { collection, query, orderBy, onSnapshot, where, doc, addDoc, serverTimestamp, getDocs } from "firebase/firestore";
import { db, uploadToStorage } from "@/firebase";
import { getStorage, uploadBytesResumable, ref as storageRef } from "firebase/storage";
import { reactive, ref, computed, onMounted, onUnmounted } from 'vue';
import { useRoute } from 'vue-router'
import useVuelidate from '@vuelidate/core';
import { required, email, maxLength, helpers } from '@vuelidate/validators';
import { v4 as uuidv4 } from 'uuid';

export default {
    setup() {
        const storage = getStorage()
        const events = ref([]);
        let unsubscribeEvents;
        let unsubscribeCategory;
        const route = useRoute()
        const eventId = computed(() => route.params.id)

        // Get Event Category that user has clicked, if not, unsubscribe
        const getEventCategory = async (eventId) => {
            if (!eventId) {
                unsubscribeCategory = function () { };
                return
            }
            unsubscribeCategory = onSnapshot(doc(db, "Events", eventId), (doc) => {
                volunteerFormData.category = doc.exists() ? doc.data().Category : "";
            });
        }

        onMounted(() => {

            // Get all events that are available for the volunteer
            const eventsCollection = collection(db, 'Events');
            const q = query(eventsCollection, where("IsArchived", "==", false), where('IsOpen', '==', false), orderBy('StartTime', "asc"));
            unsubscribeEvents = onSnapshot(q, (snapshot) => {
                events.value = snapshot.docs.map((doc) => {
                    return { id: doc.id, ...doc.data() }
                })
            });

            getEventCategory(eventId.value);
        });
        // Form Data
        const volunteerFormData = reactive({
            fullName: "",
            email: "",
            contactNumber: "",
            category: "",
            id: "",
            resume: "",
            idTitle: "",
            resumeTitle: "",
        });

        // Regex for contact number
        const validContactNumber = (contactNumber) => {
            const regex = /^[0][9][0-9]{9}$/;
            return regex.test(contactNumber);
        }

        // Check if file extension is accepted
        const isAcceptedImageFileExtension = (value) => {
            const acceptedExtensions = [
                'xbm',
                'tif',
                'jfif',
                'ico',
                'tiff',
                'gif',
                'svg',
                'jpeg',
                'svgz',
                'jpg',
                'webp',
                'png',
                'bmp',
                'pjp',
                'apng',
                'pjpeg',
                'avif'];
            const fileExtension = value.split('.').pop();
            return acceptedExtensions.includes(fileExtension);
        }

        const isAcceptedDocumentFileExtension = (value) => {
            const acceptedExtensions = [
                'pdf',
                'doc',
                'docx'];
            const fileExtension = value.split('.').pop();
            return acceptedExtensions.includes(fileExtension);
        }

        // Validation Rules
        const volunteerFormRules = {
            fullName: { required, maxLength: maxLength(300) },
            email: { required, email, maxLength: maxLength(254) },
            contactNumber: { required, validContactNumber: helpers.withMessage("Invalid Contact Number (Example: 09123456789)", validContactNumber) },
            category: { required },
            id: { required },
            resume: { required },
            idTitle: { isAcceptedFileExtension: helpers.withMessage("The file must be an image", isAcceptedImageFileExtension), maxLength: helpers.withMessage("The maximum file title length allowed is 150", maxLength(150)) },
            resumeTitle: { isAcceptedDocumentFileExtension: helpers.withMessage("The file must be a document", isAcceptedDocumentFileExtension), maxLength: helpers.withMessage("The maximum file title length allowed is 150", maxLength(150)) },
        }

        const volunteerForm$ = useVuelidate(volunteerFormRules, volunteerFormData);

        // Overlay handling
        const loading = ref(false);


        const onIdChange = (event) => {
            volunteerFormData.id = event.target.files[0];
            volunteerFormData.idTitle = event.target.files[0] ? event.target.files[0].name : null;
        }

        const onResumeChange = (event) => {
            volunteerFormData.resume = event.target.files[0];
            volunteerFormData.resumeTitle = event.target.files[0] ? event.target.files[0].name : null;
        }

        
        const submitVolunteerForm = async () => {
            loading.value = true;

            // check if all fields are valid
            const result = await volunteerForm$.value.$validate();

            // Add form to database
            if (result) {
                volunteerForm$.value.$reset();
                const eventDB = collection(db, 'Events');
                // Get the event id from Category
                const q = query(eventDB, where('Category', '==', volunteerFormData.category), where('IsArchived', '==', false));
                const querySnapshot = await getDocs(q);
                let eventAppliedId;
                querySnapshot.forEach((doc) => {
                    eventAppliedId = doc.id;
                });

                const storageResumeRef = storageRef(storage, "Volunteer Resumes/" + uuidv4() + volunteerFormData.resume.name);
                const storageIdRef = storageRef(storage, "Volunteer IDs/" + uuidv4() + volunteerFormData.id.name);
                const resumeUploadTask = uploadBytesResumable(storageResumeRef, volunteerFormData.resume);
                const idUploadTask = uploadBytesResumable(storageIdRef, volunteerFormData.id);
                const resumeDownloadUrl = await uploadToStorage(resumeUploadTask);
                const idDownloadUrl = await uploadToStorage(idUploadTask);

                const colRef = collection(db, "Volunteer Applications")
                await addDoc(colRef, {
                    Name: volunteerFormData.fullName,
                    Email: volunteerFormData.email,
                    ContactNumber: volunteerFormData.contactNumber,
                    EventAppliedId: eventAppliedId,
                    CreationTimestamp: serverTimestamp(),
                    IsAccepted: null,
                    IsArchived: false,
                    CvURL: resumeDownloadUrl,
                    ValidIdUrl: idDownloadUrl,
                })

                loading.value = false;
                alert("Form submitted successfully")

                // Reset form
                volunteerFormData.fullName = "";
                volunteerFormData.email = "";
                volunteerFormData.contactNumber = "";
                volunteerFormData.category = "";
                volunteerFormData.id = "";
                volunteerFormData.resume = "";
                //Clear id content
                document.getElementById("inputValidID").value = "";
                document.getElementById("inputResume").value = "";
                // Clear errors
                volunteerForm$.value.$reset();

            } else {
                loading.value = false;
                alert("Please check the form for errors");
            }
        }

        onUnmounted(() => {
            unsubscribeEvents();
            unsubscribeCategory();
        });

        return {
            volunteerFormData,
            volunteerForm$,
            onIdChange,
            onResumeChange,
            events,
            submitVolunteerForm,
            loading,
        }

    },
}
</script>


<style scoped src="@/assets/css/globalStyle.css"></style>
